import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import { 
  getConductors, 
  getConductorById, 
  updateConductor,
  getPendingConductors,
  approvePendingConductor,
  rejectPendingConductor,
  getPendingConductorById,
  updatePendingConductor 
} from '../../Services/Operations/ProductAPI';
import { toast } from 'react-toastify';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Name</th>
          <th>Mobile</th>
          <th>Bus Owner</th>
          <th>Assigned Bus</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div className="skeleton-profile-image"></div>
        <div className="details-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="detail-section">
              <div className="skeleton-label"></div>
              <div className="skeleton-value"></div>
            </div>
          ))}
        </div>
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonEditModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 edit-modal skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="form-group">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const ConductorManagement = () => {
  const { token } = useSelector(state => state.user);
  
  const [conductors, setConductors] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  const [viewMode, setViewMode] = useState('regular');
  const [pendingConductors, setPendingConductors] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  const [editingConductor, setEditingConductor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingConductor, setViewingConductor] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConductors, setTotalConductors] = useState(0);
  const [pageSize] = useState(10);

  const [isEditingPending, setIsEditingPending] = useState(false);

  // ✅ Ref for debounce timeout
  const searchTimeoutRef = useRef(null);

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  // ✅ FIXED: fetchConductors with debounced search term
  const fetchConductors = async () => {
    try {
      setSearching(true);
      setLoading(true);
      setError(null);
      
      const response = await getConductors(token, currentPage, pageSize, debouncedSearchTerm);

      if (response && response.conductors) {
        const transformedConductors = response.conductors.map((conductor, index) => ({
          id: conductor._id,
          serialNo: getSequentialNumber(index),
          profileImage: conductor.profileImage,
          name: conductor.name || '',
          mobileNumber: conductor.mobileNumber || '',
          assignedBus: conductor.assignedBus || null,
          busOwner: conductor.busOwner || null,
          isActive: conductor.isActive || false,
          joined: new Date(conductor.createdAt).toISOString().split('T')[0],
          status: conductor.isActive ? 'Active' : 'Inactive',
          isPending: false,
          source: 'regular'
        }));

        setConductors(transformedConductors);
        setTotalPages(response.totalPages || 1);
        setTotalConductors(response.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch conductors.');
      console.error('Error fetching conductors:', err);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchPendingConductors = async () => {
    try {
      setLoadingPending(true);
      setError(null);

      const response = await getPendingConductors(token);

      if (response && response.success) {
        const transformedConductors = response.data.map((conductor, index) => ({
          id: conductor._id,
          pendingId: conductor._id,
          serialNo: index + 1,
          profileImage: conductor.profileImage || '',
          name: conductor.name || '',
          mobileNumber: conductor.mobileNumber || '',
          assignedBus: conductor.assignedBus || null,
          busOwner: conductor.busOwner || null,
          isActive: false,
          joined: new Date(conductor.createdAt).toISOString().split('T')[0],
          status: 'Pending',
          isPending: true,
          source: 'pending',
          submittedBy: conductor.submittedBy,
          submittedByName: conductor.submittedByName,
          submittedByEmail: conductor.submittedByEmail,
          operationType: conductor.operationType,
          approvalStatus: conductor.approvalStatus,
          originalData: conductor.originalData
        }));

        setPendingConductors(transformedConductors);
        setTotalConductors(transformedConductors.length);
        setTotalPages(1);
      } else {
        setPendingConductors([]);
        setTotalConductors(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching pending conductors:', err);
      setError('Failed to fetch pending conductors. Please try again.');
      setPendingConductors([]);
    } finally {
      setLoadingPending(false);
    }
  };

  // ✅ FIXED: Effect depends on debounced search term
  useEffect(() => {
    if (viewMode === 'regular') {
      fetchConductors();
    } else {
      fetchPendingConductors();
    }
  }, [viewMode, currentPage, debouncedSearchTerm]);

  // ✅ Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const refreshPendingConductors = async () => {
    await fetchPendingConductors();
  };

  const getCurrentConductors = () => {
    return viewMode === 'regular' ? conductors : pendingConductors;
  };

  const getCurrentLoading = () => {
    return viewMode === 'regular' ? loading : loadingPending;
  };

  const currentConductors = getCurrentConductors();
  const isLoading = getCurrentLoading();

  // ✅ FIXED: No client-side filtering - server already filters
  const filteredConductors = currentConductors;

  const handleApprovePendingConductor = async (pendingId) => {
    try {
      setError(null);
      const response = await approvePendingConductor(pendingId, token);

      if (response.success) {
        toast.success('Pending conductor approved successfully!');
        setPendingConductors(prev => prev.filter(conductor => conductor.id !== pendingId));
        setTotalConductors(prev => prev - 1);
        await fetchConductors();
      } else {
        throw new Error(response.message || 'Failed to approve pending conductor');
      }
    } catch (err) {
      console.error('Error approving pending conductor:', err);
      setError('Failed to approve pending conductor. Please try again.');
      toast.error('Failed to approve pending conductor. Please try again.');
    }
  };

  const handleRejectPendingConductor = async (pendingId) => {
    if (!window.confirm('Are you sure you want to reject this pending conductor?')) return;

    try {
      setError(null);
      const response = await rejectPendingConductor(pendingId, token);

      if (response.success) {
        toast.success('Pending conductor rejected successfully!');
        setPendingConductors(prev => prev.filter(conductor => conductor.id !== pendingId));
        setTotalConductors(prev => prev - 1);
      } else {
        throw new Error(response.message || 'Failed to reject pending conductor');
      }
    } catch (err) {
      console.error('Error rejecting pending conductor:', err);
      setError('Failed to reject pending conductor. Please try again.');
      toast.error('Failed to reject pending conductor. Please try again.');
    }
  };

  const handleViewConductor = async (conductor) => {
    try {
      setIsFetchingDetails(true);
      setError(null);

      let response;
      
      if (conductor.isPending) {
        response = await getPendingConductorById(conductor.id, token);
      } else {
        response = await getConductorById(conductor.id, token);
      }
      
      if (response.success) {
        const conductorData = response.data;
        setViewingConductor({
          id: conductorData._id,
          name: conductorData.name || '',
          mobileNumber: conductorData.mobileNumber || '',
          isActive: conductorData.isActive,
          assignedBus: conductorData.assignedBus || null,
          busOwner: conductorData.busOwner || null,
          profileImage: conductorData.profileImage,
          createdAt: new Date(conductorData.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          updatedAt: new Date(conductorData.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          isPending: conductor.isPending,
          submittedBy: conductorData.submittedBy,
          submittedByName: conductorData.submittedByName,
          operationType: conductorData.operationType
        });
      } else {
        setError('Failed to fetch conductor details.');
      }
    } catch (err) {
      console.error('Error fetching conductor details:', err);
      setError('Failed to fetch conductor details.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditConductor = async (conductorId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      
      const response = await getConductorById(conductorId, token);
      
      if (response.success) {
        const conductor = response.data;
        setEditingConductor(conductor._id);
        setEditForm({
          name: conductor.name || '',
          mobileNumber: conductor.mobileNumber || '',
          isActive: conductor.isActive || true
        });
      } else {
        setError('Failed to fetch conductor details for editing.');
      }
    } catch (err) {
      console.error('Error fetching conductor details for editing:', err);
      setError('Failed to fetch conductor details for editing.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditPendingConductor = async (conductorId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      setIsEditingPending(true);

      const response = await getPendingConductorById(conductorId, token);

      if (response.success) {
        const conductor = response.data;
        setEditingConductor(conductor._id);
        setEditForm({
          name: conductor.name || '',
          mobileNumber: conductor.mobileNumber || '',
          isActive: false
        });
      } else {
        setError('Failed to fetch pending conductor details for editing.');
      }
    } catch (err) {
      console.error('Error fetching pending conductor details for editing:', err);
      setError('Failed to fetch pending conductor details for editing.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);
      
      const updateData = {
        name: editForm.name,
        mobileNumber: editForm.mobileNumber,
        ...(isEditingPending ? {} : { isActive: editForm.isActive })
      };

      const response = isEditingPending
        ? await updatePendingConductor(editingConductor, updateData, token)
        : await updateConductor(editingConductor, updateData, token);
      
      if (response.success) {
        if (isEditingPending) {
          setPendingConductors(prevConductors =>
            prevConductors.map(conductor =>
              conductor.id === editingConductor ? {
                ...conductor,
                name: response.data.name,
                mobileNumber: response.data.mobileNumber,
              } : conductor
            )
          );
          toast.success('Pending conductor updated successfully!');
        } else {
          setConductors(prevConductors =>
            prevConductors.map(conductor =>
              conductor.id === editingConductor ? {
                ...conductor,
                name: response.data.name,
                mobileNumber: response.data.mobileNumber,
                status: response.data.isActive ? 'Active' : 'Inactive',
                isActive: response.data.isActive
              } : conductor
            )
          );
          alert('Conductor updated successfully!');
        }

        setEditingConductor(null);
        setEditForm({});
        setIsEditingPending(false);
      } else {
        setError(response.message || 'Failed to update conductor.');
      }
    } catch (err) {
      console.error('Error updating conductor:', err);
      setError(err.response?.data?.message || 'Failed to update conductor. Please try again.');
      if (!isEditingPending) alert('Failed to update conductor. Please try again.');
      else toast.error('Failed to update pending conductor. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingConductor(null);
    setEditForm({});
    setError(null);
    setIsEditingPending(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const closeViewModal = () => {
    setViewingConductor(null);
  };

  // ✅ FIXED: Debounced search with page reset
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 500);
  };

  // ✅ FIXED: handleRefresh clears everything
  const handleRefresh = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (viewMode === 'regular') {
      fetchConductors();
    } else {
      fetchPendingConductors();
    }
  };

  const handleExportCSV = () => {
    if (currentConductors.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Serial No.', 'ID', 'Name', 'Mobile', 'Bus Owner', 'Assigned Bus', 'Status', 'Joined'];
    const csvData = currentConductors.map(conductor => [
      conductor.serialNo,
      conductor.id,
      conductor.name || '',
      conductor.mobileNumber || '',
      conductor.busOwner?.name || 'N/A',
      conductor.assignedBus?.busNumber || 'N/A',
      conductor.status,
      conductor.joined
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conductors-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading && currentConductors.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Conductor Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search conductors by name, mobile, or bus owner..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "70%" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>

          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
        {viewMode === 'regular' ? (
          <>Page: {currentPage} | Total Pages: {totalPages} | Total Conductors: {totalConductors}</>
        ) : (
          <>Pending Conductors: {pendingConductors.length} | {loadingPending ? 'Loading...' : ''}</>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total {viewMode === 'regular' ? 'Conductors' : 'Pending Conductors'}</h3>
          <p>{totalConductors}</p>
          <span>{viewMode === 'regular' ? 'All registered conductors' : 'Awaiting approval'}</span>
        </div>
        <div className="stat-card">
          <h3>Active Conductors</h3>
          <p>{viewMode === 'regular' ? conductors.filter(c => c.status === 'Active').length : 0}</p>
          <span>Active conductors</span>
        </div>
        <div className="stat-card">
          <h3>With Assigned Bus</h3>
          <p>{viewMode === 'regular' ? conductors.filter(c => c.assignedBus).length : 0}</p>
          <span>Conductors with bus assignment</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{filteredConductors.length}</p>
          <span>conductors found</span>
        </div>
      </div>

      <div className="view-toggle-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '0 15px' }}>
        <button
          className={`toggle-btn ${viewMode === 'regular' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('regular');
            setCurrentPage(1);
            setSearchTerm('');
            setDebouncedSearchTerm('');
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #007bff',
            backgroundColor: viewMode === 'regular' ? '#007bff' : 'transparent',
            color: viewMode === 'regular' ? 'white' : '#007bff',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s'
          }}
        >
          👤 Regular
        </button>
        <button
          className={`toggle-btn ${viewMode === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('pending');
            setCurrentPage(1);
            setSearchTerm('');
            setDebouncedSearchTerm('');
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #ff9800',
            backgroundColor: viewMode === 'pending' ? '#ff9800' : 'transparent',
            color: viewMode === 'pending' ? 'white' : '#ff9800',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s',
            position: 'relative'
          }}
        >
          📋 Executive Submitted
          {viewMode === 'pending' && pendingConductors.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'red',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              {pendingConductors.length}
            </span>
          )}
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Bus Owner</th>
              <th>Assigned Bus</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredConductors.length > 0 ? (
              filteredConductors.map(conductor => (
                <tr key={conductor.id} className={conductor.isPending ? 'pending-row' : ''}>
                  <td>{conductor.serialNo}</td>
                  <td>
                    {!conductor.isPending && editingConductor === conductor.id ? (
                      <input
                        type="text"
                        name="name"
                        value={editForm.name || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Name"
                      />
                    ) : (
                      <>
                        {conductor.name || 'N/A'}
                      </>
                    )}
                  </td>
                  <td>
                    {!conductor.isPending && editingConductor === conductor.id ? (
                      <input
                        type="text"
                        name="mobileNumber"
                        value={editForm.mobileNumber || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Mobile Number"
                      />
                    ) : (
                      conductor.mobileNumber || 'N/A'
                    )}
                  </td>
                  <td>
                    {conductor.busOwner?.name || 'N/A'}
                  </td>
                  <td>
                    {conductor.assignedBus ? (
                      <div className="bus-info">
                        <div><strong>{conductor.assignedBus.busNumber}</strong></div>
                        <small>{conductor.assignedBus.busName}</small>
                      </div>
                    ) : 'Not Assigned'}
                  </td>
                  <td>
                    {!conductor.isPending && editingConductor === conductor.id ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={editForm.isActive || false}
                          onChange={handleInputChange}
                        />
                        <span className="checkbox-text">
                          {editForm.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    ) : (
                      <span className={`status-badge ${conductor.isPending ? 'pending' : conductor.status?.toLowerCase() || 'inactive'}`}>
                        {conductor.isPending ? 'Pending Approval' : conductor.status || 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td>
                    {!conductor.isPending ? (
                      editingConductor === conductor.id ? (
                        <div className="edit-actions">
                          <button className="action-btn save" onClick={handleSaveEdit} disabled={isFetchingDetails}>
                            {isFetchingDetails ? 'Saving...' : 'Save'}
                          </button>
                          <button className="action-btn cancel" onClick={handleCancelEdit} disabled={isFetchingDetails}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="action-btn view"
                            onClick={() => handleViewConductor(conductor)}
                            disabled={isFetchingDetails}
                          >
                            {isFetchingDetails ? 'Loading...' : 'View'}
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleEditConductor(conductor.id)}
                            disabled={isFetchingDetails}
                            style={{ backgroundColor: '#6f42c1', color: 'white' }}
                          >
                            {isFetchingDetails ? 'Loading...' : 'Edit'}
                          </button>
                        </div>
                      )
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewConductor(conductor)}
                          style={{ backgroundColor: '#17a2b8' }}
                        >
                          View
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditPendingConductor(conductor.id)}
                          disabled={isFetchingDetails}
                          style={{ backgroundColor: '#6f42c1', color: 'white' }}
                        >
                          {isFetchingDetails ? 'Loading...' : 'Edit'}
                        </button>
                        <button
                          className="action-btn approve"
                          onClick={() => handleApprovePendingConductor(conductor.id)}
                          style={{ backgroundColor: '#28a745' }}
                        >
                          Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleRejectPendingConductor(conductor.id)}
                          style={{ backgroundColor: '#dc3545' }}
                        >
                          Reject
                        </button>
                        {conductor.submittedByName && (
                          <div style={{
                            fontSize: '10px',
                            color: '#666',
                            marginTop: '4px',
                            display: 'block'
                          }}>
                            By: {conductor.submittedByName}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  <p>
                    {viewMode === 'regular'
                      ? `No conductors found ${searchTerm ? 'matching your search' : ''}.`
                      : 'No pending conductors from executive submissions.'}
                  </p>
                  {searchTerm && (
                    <button className="clear-filters-link" onClick={handleRefresh}>
                      Clear search to see all conductors
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewMode === 'regular' && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {conductors.length} of {totalConductors} conductors
            (Page {currentPage} of {totalPages})
            {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageClick(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {viewingConductor && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Conductor Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="profile-image-section">
                  <img
                    src={viewingConductor.profileImage ? 
                      `${process.env.REACT_APP_BASE_URL}/uploads/${viewingConductor.profileImage}` : 
                      '/default-profile.png'}
                    alt={viewingConductor.name || 'Conductor'}
                    className="profile-image-large"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-profile.png';
                    }}
                  />
                </div>
                
                <div className="details-grid">
                  <div className="detail-section">
                    <h4>Name:</h4>
                    <p>{viewingConductor.name || 'N/A'}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h4>Mobile Number:</h4>
                    <p>{viewingConductor.mobileNumber || 'N/A'}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h4>Status:</h4>
                    <span className={`status-badge ${viewingConductor.isActive ? 'active' : 'inactive'}`}>
                      {viewingConductor.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  {viewingConductor.busOwner && (
                    <div className="detail-section full-width">
                      <h4>Bus Owner:</h4>
                      <div className="bus-owner-details">
                        <p><strong>Name:</strong> {viewingConductor.busOwner.name}</p>
                        <p><strong>Phone:</strong> {viewingConductor.busOwner.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {viewingConductor.assignedBus && (
                    <div className="detail-section full-width">
                      <h4>Assigned Bus:</h4>
                      <div className="bus-details">
                        <p><strong>Bus Number:</strong> {viewingConductor.assignedBus.busNumber}</p>
                        <p><strong>Bus Name:</strong> {viewingConductor.assignedBus.busName}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="detail-section">
                    <h4>Joined Date:</h4>
                    <p>{viewingConductor.createdAt}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h4>Last Updated:</h4>
                    <p>{viewingConductor.updatedAt}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingConductor && isFetchingDetails && <SkeletonModal />}

      {editingConductor && !isFetchingDetails && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditingPending ? 'Edit Pending Conductor' : 'Edit Conductor'}</h3>
              <button className="modal-close-btn" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Conductor Name"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile Number:</label>
                  <input
                    type="text"
                    name="mobileNumber"
                    value={editForm.mobileNumber || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Mobile Number"
                  />
                </div>
                {!isEditingPending && (
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={editForm.isActive || false}
                        onChange={handleInputChange}
                      />
                      <span className="checkbox-text">Active Conductor</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {editingConductor && isFetchingDetails && <SkeletonEditModal />}
    </div>
  );
};

export default ConductorManagement;